/**
 * PbDashboard — Dashboard-Übersichtsansicht für einen PB (erster Reiter).
 *
 * Pro Widget konfigurierbar (Persistenz via /v2/admin/me/dashboard-config,
 * Key `pb_dashboard`):
 *   - visible: ja/nein (Toggle)
 *   - mode: 'compact' = kompakte Vorschaukarte | 'expanded' = echtes Tab-Element eingebettet
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { BRAND, BRAND_LIGHT, getThemeColors } from '../../../lib/theme';
import { type V1User } from '../../../lib/v1';
import {
  usePbDashboardConfig,
  type PbDashboardWidgetId,
} from '../../../lib/usePbDashboardConfig';
import { AufgabenNotizenTab } from '../tabs/AufgabenNotizenTab';
import { KommunikationTab } from '../tabs/KommunikationTab';
import { BetreuungenTab } from '../tabs/BetreuungenTab';
import { MedienTab } from '../tabs/MedienTab';
import { BewertungenTab } from '../tabs/BewertungenTab';
import { EditDrawer } from '../../../components/ui/EditDrawer';
import { CareEditForm } from '../profile/CareEditForm';
import { JahresuebersichtCard } from '../JahresuebersichtCard';
import type { PbTabId } from '../PbDetailPage';
import { type TaskRow, type NoticeRow, type UploadRow, type CareRow } from './types';
import { useActiveAdmins } from './helpers';
import { AufgabenWidget, TaskEditForm } from './AufgabenWidget';
import { NotizenWidget, NoticeEditForm } from './NotizenWidget';
import { isDeceased } from '../../../lib/noticePin';
import { BetreuungenWidget } from './BetreuungenWidget';
import { NachrichtenWidget } from './NachrichtenWidget';
import { GespraechsprotokolleWidget } from './GespraechsprotokolleWidget';
import { Gespraechsprotokolle } from '../../../components/calls/Gespraechsprotokolle';
import { DokumenteWidget } from './DokumenteWidget';
import { MedienWidget } from './MedienWidget';
import { BewertungenWidget } from './BewertungenWidget';
import { ExpandedCard, SettingsPanel, ResizeControls } from './SettingsPanel';
import { MatchingList } from '../../../components/matching/MatchingList';
import { navigateGlobal } from '../../../lib/route';
import { consumeCareCreateHandoff } from '../../../lib/careCreateHandoff';
import { CreateCareModal } from '../tabs/BetreuungenTab/CreateCareModal';

/* ─── Props ──────────────────────────────────────────────────────────────────── */

interface Props {
  user: V1User | null;
  dark: boolean;
  onSelectTab: (tab: PbTabId) => void;
  /**
   * Pflegeempfänger? Bei `false` (Kontaktperson) entfallen die
   * betreuungsbezogenen Dashboard-Teile: die Jahresübersicht und das
   * Betreuungen-Widget. Die übrigen Widgets (Aufgaben, Notizen, Nachrichten,
   * Dokumente …) gelten auch für Kontakte.
   */
  careRecipient?: boolean;
  /** Profil-User geändert (z. B. Startdatum aus „Neue Betreuung anlegen“). */
  onUserSaved?: (u: V1User) => void;
}

/** Widgets, die nur für Pflegeempfänger Sinn ergeben. */
const CARE_ONLY_WIDGETS = new Set<string>(['betreuungen']);

/* ─── Drawer-State ─────────────────────────────────────────────────────── */

type DrawerState =
  | { type: 'care'; care: CareRow }
  | { type: 'task'; task: TaskRow; userId: number }
  | { type: 'notice'; notice: NoticeRow; userId: number }
  | null;

/* ─── Hauptkomponente ────────────────────────────────────────────────────── */

export function PbDashboard({ user, dark, onSelectTab, careRecipient = true, onUserSaved }: Props) {
  const handlePbStartdatumChanged = useCallback((startdatum: string) => {
    if (user) onUserSaved?.({ ...user, startdatum } as V1User);
  }, [user, onUserSaved]);
  const { t } = useTranslation();
  const tc = getThemeColors(dark);

  // Batch-12b: Cares werden von der gemeinsamen JahresuebersichtCard geladen
  // und hierher gespiegelt (fürs BetreuungenWidget). ylReload stößt ein
  // Neu-Laden der Karte an (z.B. nach Widget-Edits).
  const [cares, setCares] = useState<CareRow[]>([]);
  const [caresLoading, setCaresLoading] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [ylReload, setYlReload] = useState(0);
  // Erweitertes Betreuungen-Widget: Liste neu laden, wenn die Jahresübersicht
  // oben etwas geändert hat (z. B. neue Betreuung angelegt).
  const [caresListReload, setCaresListReload] = useState(0);

  const {
    config: _cfg, loading: configLoading, widgetSettings, setWidgetSettings,
    widgetOrder, dragEnabled, setWidgetOrder, setDragEnabled,
    resizeEnabled, setResizeEnabled, widgetSize, setWidgetSize, areaScope,
  } = usePbDashboardConfig();

  const admins = useActiveAdmins();

  const [draggingId, setDraggingId] = useState<PbDashboardWidgetId | null>(null);
  const [dragOverId, setDragOverId] = useState<PbDashboardWidgetId | null>(null);

  const [drawerState, setDrawerState] = useState<DrawerState>(null);
  const drawerOpen = drawerState !== null;
  const closeDrawer = useCallback(() => setDrawerState(null), []);

  const reloadCaresRef = useRef<(() => void) | null>(null);
  const reloadTasksRef = useRef<(() => void) | null>(null);
  const reloadNoticesRef = useRef<(() => void) | null>(null);

  const [localMedia, setLocalMedia] = useState<UploadRow[]>([]);
  const [localDocuments, setLocalDocuments] = useState<UploadRow[]>([]);
  useEffect(() => {
    const rawMedia = ((user as Record<string, unknown>)?.['media'] as UploadRow[] | undefined) ?? [];
    const rawDocs = ((user as Record<string, unknown>)?.['documents'] as UploadRow[] | undefined) ?? [];
    setLocalMedia(rawMedia);
    setLocalDocuments(rawDocs);
  }, [user?.id]);

  // Batch-12b: Die JahresuebersichtCard lädt Cares/Abwesenheiten/Aufgaben/
  // Verordnungen selbst; der Ref stößt nur noch ihr Neu-Laden an
  // (Widget-Edits, Care-Drawer). Cares spiegelt sie via onCaresLoaded hierher.
  useEffect(() => {
    reloadCaresRef.current = () => setYlReload(x => x + 1);
  }, []);
  const reloadTimelineTasks = useCallback(() => setYlReload(x => x + 1), []);

  // Matching → Betreuung anlegen: BK-Vorauswahl aus dem Matching-Board
  // (sessionStorage-Handoff) oder Klick im Matching-Widget öffnet den
  // "Neue Betreuung anlegen"-Drawer mit der BK vorausgewählt.
  const [careCreateBkId, setCareCreateBkId] = useState<number | null>(null);
  useEffect(() => {
    if (user?.id == null) return;
    const handoff = consumeCareCreateHandoff(user.id);
    if (handoff) setCareCreateBkId(handoff.bkUserId);
  }, [user?.id]);

  if (!user) {
    return (
      <div style={{ padding: 32, textAlign: 'center', color: tc.muted, fontSize: 13 }}>
        {t('pflege.loading')}
      </div>
    );
  }

  const ws = (id: PbDashboardWidgetId) => widgetSettings(id);

  const drawerTitle = drawerState?.type === 'care'
    ? t('pflege.detail.dashboard.betreuungen')
    : drawerState?.type === 'task'
    ? t('pflege.detail.dashboard.aufgaben')
    : drawerState?.type === 'notice'
    ? t('pflege.detail.dashboard.notizen')
    : '';

  return (
    <>
    {/* Edit Drawer — care/task/notice */}
    <EditDrawer open={drawerOpen} onClose={closeDrawer} title={drawerTitle} dark={dark}>
      {drawerState?.type === 'care' && user && (
        <CareEditForm
          dark={dark}
          care={{ ...drawerState.care, pb_user_id: drawerState.care.pb_user_id ?? user.id }}
          onPbStartdatumChanged={handlePbStartdatumChanged}
          onSaved={() => {
            closeDrawer();
            reloadCaresRef.current?.();
          }}
          onCancel={closeDrawer}
        />
      )}
      {drawerState?.type === 'task' && (
        <TaskEditForm
          dark={dark}
          task={drawerState.task}
          userId={drawerState.userId}
          admins={admins}
          onSaved={() => { closeDrawer(); reloadTasksRef.current?.(); reloadTimelineTasks(); }}
          onCancel={closeDrawer}
        />
      )}
      {drawerState?.type === 'notice' && (
        <NoticeEditForm
          dark={dark}
          notice={drawerState.notice}
          onSaved={() => { closeDrawer(); reloadNoticesRef.current?.(); }}
          onCancel={closeDrawer}
        />
      )}
    </EditDrawer>
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20, paddingBottom: 24 }}>

      {/* ── Header mit Einstellungs-Button ──────────────────────────── */}
      <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}>
        <button
          onClick={() => setShowSettings(v => !v)}
          title={t('pflege.detail.dashboard.settings_title')}
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 6,
            background: showSettings ? BRAND_LIGHT : 'transparent',
            border: `1px solid ${showSettings ? BRAND : tc.border}`,
            borderRadius: 8, padding: '6px 13px', cursor: 'pointer',
            fontSize: 12, fontFamily: 'Outfit,sans-serif',
            color: showSettings ? BRAND : tc.muted,
            transition: 'all 0.15s',
          }}
        >
          ⚙ {t('pflege.detail.dashboard.settings_title')}
        </button>
      </div>

      {/* ── Einstellungs-Panel ───────────────────────────────────────── */}
      {showSettings && !configLoading && (
        <SettingsPanel
          dark={dark}
          widgetSettings={ws}
          setWidgetSettings={setWidgetSettings}
          dragEnabled={dragEnabled}
          setDragEnabled={setDragEnabled}
          resizeEnabled={resizeEnabled}
          setResizeEnabled={setResizeEnabled}
          areaScope={areaScope}
          onClose={() => setShowSettings(false)}
        />
      )}

      {/* ── Jahresübersicht — gemeinsame Karte (Batch-12b).
             Zeigt Betreuungen/Abwesenheiten über das Jahr; für eine
             Kontaktperson gibt es beides nicht. ───────────────────────── */}
      {careRecipient && (
        <JahresuebersichtCard
          user={user}
          collapsible
          collapseKey="pb-dashboard"
          dark={dark}
          reloadSignal={ylReload}
          onCaresLoaded={(rows, loading) => { setCares(rows as CareRow[]); setCaresLoading(loading); }}
          onTasksChanged={() => reloadTasksRef.current?.()}
          onPbStartdatumChanged={handlePbStartdatumChanged}
          onChanged={() => setCaresListReload(x => x + 1)}
        />
      )}

      {/* ── Widget-Grid ──────────────────────────────────────────────── */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 14 }}>
        {widgetOrder
          .filter(id => ws(id).visible)
          .filter(id => careRecipient || !CARE_ONLY_WIDGETS.has(id))
          .map(id => {
          const widgetContent = (() => {
            switch (id) {
              case 'betreuungen':
                return ws('betreuungen').mode === 'expanded' ? (
                  <ExpandedCard dark={dark} title={t('pflege.detail.dashboard.betreuungen')} onShowAll={(e) => { e.stopPropagation(); onSelectTab('betreuungen'); }} showAllLabel={t('pflege.detail.dashboard.alle_anzeigen')}>
                    {/* Ohne eigene Jahresübersicht — die steht bereits oben (sonst doppelt). */}
                    <BetreuungenTab
                      user={user} dark={dark} hideTimeline
                      reloadSignal={caresListReload}
                      onChanged={() => setYlReload(x => x + 1)}
                      onPbStartdatumChanged={handlePbStartdatumChanged}
                    />
                  </ExpandedCard>
                ) : (
                  <BetreuungenWidget
                    dark={dark} cares={cares} loading={caresLoading}
                    onShowAll={(e) => { e.stopPropagation(); onSelectTab('betreuungen'); }}
                    onEditCare={(care) => setDrawerState({ type: 'care', care })}
                  />
                );
              case 'aufgaben':
                return ws('aufgaben').mode === 'expanded' ? (
                  <ExpandedCard dark={dark} title={t('pflege.detail.dashboard.aufgaben')} onShowAll={(e) => { e.stopPropagation(); onSelectTab('aufgaben-infos'); }} showAllLabel={t('pflege.detail.dashboard.alle_anzeigen')}>
                    <AufgabenNotizenTab user={user} dark={dark} only="tasks" />
                  </ExpandedCard>
                ) : (
                  <AufgabenWidget
                    dark={dark} userId={user.id} admins={admins}
                    onShowAll={(e) => { e.stopPropagation(); onSelectTab('aufgaben-infos'); }}
                    onEditTask={(task) => setDrawerState({ type: 'task', task, userId: user.id })}
                    reloadRef={reloadTasksRef}
                    onChanged={reloadTimelineTasks}
                  />
                );
              case 'notizen':
                return ws('notizen').mode === 'expanded' ? (
                  <ExpandedCard dark={dark} title={t('pflege.detail.dashboard.notizen')} onShowAll={(e) => { e.stopPropagation(); onSelectTab('aufgaben-infos'); }} showAllLabel={t('pflege.detail.dashboard.alle_anzeigen')}>
                    <AufgabenNotizenTab user={user} dark={dark} only="notices" />
                  </ExpandedCard>
                ) : (
                  <NotizenWidget
                    dark={dark} userId={user.id}
                    onShowAll={(e) => { e.stopPropagation(); onSelectTab('aufgaben-infos'); }}
                    onEditNotice={(notice) => setDrawerState({ type: 'notice', notice, userId: user.id })}
                    reloadRef={reloadNoticesRef}
                    isDead={isDeceased(user)}
                  />
                );
              case 'nachrichten':
                return ws('nachrichten').mode === 'expanded' ? (
                  <ExpandedCard dark={dark} title={t('pflege.detail.dashboard.nachrichten')} onShowAll={(e) => { e.stopPropagation(); onSelectTab('kommunikation'); }} showAllLabel={t('pflege.detail.dashboard.alle_anzeigen')}>
                    <KommunikationTab user={user} dark={dark} />
                  </ExpandedCard>
                ) : (
                  <NachrichtenWidget dark={dark} userId={user.id} onShowAll={(e) => { e.stopPropagation(); onSelectTab('kommunikation'); }} />
                );
              case 'gespraechsprotokolle':
                return ws('gespraechsprotokolle').mode === 'expanded' ? (
                  <ExpandedCard dark={dark} title={t('pflege.detail.dashboard.gespraechsprotokolle')} onShowAll={(e) => { e.stopPropagation(); onSelectTab('kommunikation'); }} showAllLabel={t('pflege.detail.dashboard.alle_anzeigen')}>
                    <Gespraechsprotokolle userId={user.id} dark={dark} variant="plain" maxHeight={420} />
                  </ExpandedCard>
                ) : (
                  <GespraechsprotokolleWidget dark={dark} userId={user.id} onShowAll={(e) => { e.stopPropagation(); onSelectTab('kommunikation'); }} />
                );
              case 'dokumente':
                return ws('dokumente').mode === 'expanded' ? (
                  <ExpandedCard dark={dark} title={t('pflege.detail.dashboard.dokumente')} onShowAll={(e) => { e.stopPropagation(); onSelectTab('medien-dokumente'); }} showAllLabel={t('pflege.detail.dashboard.alle_anzeigen')}>
                    <MedienTab user={user} dark={dark} />
                  </ExpandedCard>
                ) : (
                  <DokumenteWidget dark={dark} userId={user.id} initialDocuments={localDocuments} onShowAll={(e) => { e.stopPropagation(); onSelectTab('medien-dokumente'); }} />
                );
              case 'medien':
                return ws('medien').mode === 'expanded' ? (
                  <ExpandedCard dark={dark} title={t('pflege.detail.dashboard.medien')} onShowAll={(e) => { e.stopPropagation(); onSelectTab('medien-dokumente'); }} showAllLabel={t('pflege.detail.dashboard.alle_anzeigen')}>
                    <MedienTab user={user} dark={dark} />
                  </ExpandedCard>
                ) : (
                  <MedienWidget dark={dark} userId={user.id} initialMedia={localMedia} onShowAll={(e) => { e.stopPropagation(); onSelectTab('medien-dokumente'); }} />
                );
              case 'bewertungen':
                return ws('bewertungen').mode === 'expanded' ? (
                  <ExpandedCard dark={dark} title={t('pflege.detail.dashboard.bewertungen')} onShowAll={(e) => { e.stopPropagation(); onSelectTab('bewertungen'); }} showAllLabel={t('pflege.detail.dashboard.alle_anzeigen')}>
                    <BewertungenTab user={user} dark={dark} />
                  </ExpandedCard>
                ) : (
                  <BewertungenWidget dark={dark} userId={user.id} userRole={user.user_role ?? 2} onShowAll={(e) => { e.stopPropagation(); onSelectTab('bewertungen'); }} />
                );
              case 'matching':
                return (
                  <ExpandedCard dark={dark} title={t('pflege.detail.dashboard.matching')} onShowAll={(e) => { e.stopPropagation(); navigateGlobal('matching'); }} showAllLabel={t('pflege.detail.dashboard.alle_anzeigen')}>
                    <div style={{ padding: '4px 2px' }}>
                      {/* Klick auf eine BK öffnet "Neue Betreuung anlegen" mit
                          dieser BK vorausgewählt (wie im Matching-Board). */}
                      <MatchingList
                        userId={user.id}
                        dark={dark}
                        mode="compact"
                        limit={5}
                        onSelect={c => setCareCreateBkId(c.id)}
                      />
                    </div>
                  </ExpandedCard>
                );
              default:
                return null;
            }
          })();

          if (!widgetContent) return null;

          const size = widgetSize(id);
          const spanStyle: React.CSSProperties = {
            gridColumn: size.w > 1 ? `span ${size.w}` : undefined,
            gridRow: size.h > 1 ? `span ${size.h}` : undefined,
          };

          const resizeAffordance = resizeEnabled ? (
            <ResizeControls dark={dark} size={size} onChange={(s) => setWidgetSize(id, s)} />
          ) : null;

          if (dragEnabled) {
            const isOver = dragOverId === id && draggingId !== id;
            return (
              <div
                key={id}
                draggable
                onDragStart={() => setDraggingId(id)}
                onDragEnd={() => { setDraggingId(null); setDragOverId(null); }}
                onDragOver={e => { e.preventDefault(); setDragOverId(id); }}
                onDrop={e => {
                  e.preventDefault();
                  if (!draggingId || draggingId === id) return;
                  const current = widgetOrder;
                  const fromIdx = current.indexOf(draggingId);
                  const toIdx = current.indexOf(id);
                  if (fromIdx < 0 || toIdx < 0) return;
                  const next = [...current];
                  next.splice(fromIdx, 1);
                  next.splice(toIdx, 0, draggingId);
                  setWidgetOrder(next);
                  setDraggingId(null);
                  setDragOverId(null);
                }}
                style={{
                  ...spanStyle,
                  cursor: 'grab',
                  opacity: draggingId === id ? 0.45 : 1,
                  outline: isOver ? `2px dashed ${BRAND}` : 'none',
                  outlineOffset: 3,
                  borderRadius: 12,
                  transition: 'opacity 0.15s, outline 0.1s',
                }}
              >
                {resizeAffordance}
                {widgetContent}
              </div>
            );
          }

          if (!resizeAffordance && size.w <= 1 && size.h <= 1) {
            return <React.Fragment key={id}>{widgetContent}</React.Fragment>;
          }

          return (
            <div key={id} style={spanStyle}>
              {resizeAffordance}
              {widgetContent}
            </div>
          );
        })}
      </div>

    </div>

    {/* "Neue Betreuung anlegen" mit vorausgewählter BK (Matching-Board-Handoff
        oder Klick im Matching-Widget). */}
    {careCreateBkId != null && (
      <CreateCareModal
        dark={dark}
        pbUserId={user.id}
        initialPartnerId={careCreateBkId}
        onClose={() => setCareCreateBkId(null)}
        onCreated={() => { setCareCreateBkId(null); reloadCaresRef.current?.(); }}
        onPbStartdatumChanged={handlePbStartdatumChanged}
      />
    )}
    </>
  );
}
